import { BOOKINGS_APP_DEF_ID } from '@wix/bookings-catalog-calendar-viewer-utils';
import { WidgetViewModelFactory } from '../viewModel';
import { ViewModelFactoryArgs } from '../../state/types';
import { ViewModelSubscription } from '../initViewModelSubscriptions';

export enum DialogState {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
}

type BaseDialogViewModel<Type, Data> = {
  isOpen: boolean;
  data: Data;
  type: Type;
  state: DialogState;
};

export type GeneralDialogViewModel = BaseDialogViewModel<
  DialogType,
  {
    titleText?: string;
    contentText: string;
    confirmButtonText: string;
    confirmButtonUrlLink?: string;
    closeButtonText?: string;
  }
>;

export type DialogViewModel = GeneralDialogViewModel;

export enum DialogType {
  JoinWaitlist = 'join-waitlist',
  PremiumViewer = 'premium-viewer',
  PremiumPreview = 'premium-preview',
}

export const MOBILE_APP_INVITE_LENDING_PAGE_URL_PREFIX =
  'https://apps.wix.com/place-invites/join-lp/';

export const createDialogViewModel: WidgetViewModelFactory<DialogViewModel> = ({
  state,
  flowAPI,
}) => {
  const { dialog } = state;
  if (!dialog) {
    return {
      isOpen: false,
      data: { titleText: '', contentText: '', confirmButtonText: '' },
      type: DialogType.JoinWaitlist,
      state: DialogState.IDLE,
    };
  }

  const { type } = dialog;

  const confirmButtonUrlLink = getUrl({ state, flowAPI });

  return {
    isOpen: true,
    data: {
      ...getTexts({ state, flowAPI }),
      ...(confirmButtonUrlLink ? { confirmButtonUrlLink } : {}),
    },
    type,
    state: DialogState.IDLE,
  };
};

const getTexts = ({
  state: { dialog },
  flowAPI: {
    translations: { t },
  },
}: ViewModelFactoryArgs) => {
  switch (dialog?.type) {
    case DialogType.JoinWaitlist:
      return {
        titleText: t('app.dialog.join-waitlist.title'),
        contentText: t('app.dialog.join-waitlist.content'),
        confirmButtonText: t('app.dialog.join-waitlist.button'),
        closeButtonText: t('app.dialog.join-waitlist.close-button'),
      };
    case DialogType.PremiumViewer:
      return {
        contentText: t('app.dialog.premium-viewer.content'),
        confirmButtonText: t('app.dialog.premium-viewer.button'),
      };
    case DialogType.PremiumPreview:
      return {
        titleText: t('app.dialog.premium-preview.title'),
        contentText: t('app.dialog.premium-preview.content'),
        confirmButtonText: t('app.dialog.premium-preview.confirm-button'),
      };
    default:
      return {
        titleText: '',
        contentText: '',
        confirmButtonText: '',
      };
  }
};

const getUrl = ({
  state: { dialog },
  flowAPI: {
    environment: { isEditorX },
    controllerConfig: {
      appParams: { instanceId },
      platformAPIs: { bi },
    },
  },
}: ViewModelFactoryArgs) => {
  if (dialog?.type === DialogType.PremiumPreview) {
    const referral = `bookings_previewsite_bookflowpopup`;
    const baseUrl = isEditorX
      ? 'https://manage.editorx.com/wix/api'
      : 'https://premium.wix.com/wix/api';

    return `${baseUrl}/premiumStart?appDefId=${BOOKINGS_APP_DEF_ID}&instanceId=${instanceId}&referralAdditionalInfo=${referral}`;
  }

  return dialog?.type === DialogType.JoinWaitlist
    ? MOBILE_APP_INVITE_LENDING_PAGE_URL_PREFIX + bi?.metaSiteId
    : '';
};

export const subscribeToDialogViewModel: ViewModelSubscription = ({
  setViewModel,
  subscribe,
  flowAPI,
  getState,
}) => {
  subscribe(
    ({ dialog }) => ({
      dialog,
    }),
    () => {
      const dialogViewModel = createDialogViewModel({
        state: getState(),
        flowAPI,
      });
      setViewModel({ dialogViewModel });
    },
  );
};
